import { toolHolderTypes } from '@/features/traceability/data/toolHolderOptions';
import { lubricationTypes } from '@/features/traceability/data/lubricationOptions';

/**
 * Format a duration in seconds to a human-readable string.
 * @param seconds Number of seconds to format.
 * @returns Formatted duration string.
 */
export const formatDuration = (seconds: number | null) => {
  if (seconds === null || seconds === undefined) return 'N/A';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}min`;
  return `${(seconds / 3600).toFixed(1)}h`;
};

/**
 * Format a number with specified decimal places and French locale.
 * @param num The number to format.
 * @param decimals Number of decimal places (default is 1).
 * @returns Formatted number string.
 */
export const formatNumber = (num: number, decimals = 1) => {
  return num.toLocaleString('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Format volume value in cm³, L, or m³
 * Automatically converts to appropriate unit based on magnitude.
 *
 * @param cm3 - Volume in cubic centimeters
 * @returns Formatted string with appropriate unit (cm³, L, or m³)
 *
 * @example
 * formatVolume(500)       // "500.00 cm³"
 * formatVolume(15000)     // "15.00 L"
 * formatVolume(2000000)   // "2.00 m³"
 * formatVolume(null)      // "N/A"
 */
export function formatVolume(cm3: number | null): string {
  if (cm3 === null || cm3 === undefined) return 'N/A';

  // For very large volumes, convert to cubic meters (1,000,000 cm³ = 1 m³)
  if (cm3 >= 1000000) {
    return `${(cm3 / 1000000).toFixed(2)} m³`;
  }

  // For large volumes, convert to liters (1,000 cm³ = 1 L)
  if (cm3 >= 10000) {
    return `${(cm3 / 1000).toFixed(2)} L`;
  }

  return `${cm3.toFixed(2)} cm³`;
}

/**
 * Format distance value in mm, m, or km
 * Automatically converts to appropriate unit based on magnitude.
 *
 * @param mm - Distance in millimeters
 * @returns Formatted string with appropriate unit (mm, m, or km)
 *
 * @example
 * formatDistance(500)      // "500.00 mm"
 * formatDistance(15000)    // "15.00 m"
 * formatDistance(2500000)  // "2.50 km"
 * formatDistance(null)     // "N/A"
 */
export function formatDistance(mm: number | null): string {
  if (mm === null || mm === undefined) return 'N/A';

  // For very long distances, convert to kilometers (1,000,000 mm = 1 km)
  if (mm >= 1000000) {
    return `${(mm / 1000000).toFixed(2)} km`;
  }

  // For long distances, convert to meters (1,000 mm = 1 m)
  if (mm >= 1000) {
    return `${(mm / 1000).toFixed(2)} m`;
  }

  return `${mm.toFixed(2)} mm`;
}

/**
 * Format malround value in µm (micrometers)
 * @param micrometers - Malround in micrometers
 * @returns Formatted string with "µm" unit
 */
function formatMalround(micrometers: number | null): string {
  if (micrometers === null || micrometers === undefined) return 'N/A';

  return `${micrometers.toFixed(2)} µm`;
}

/**
 * Format pieces count (integer)
 * @param count - Number of pieces
 * @returns Formatted string with "pcs" unit
 */
function formatPieces(count: number | null): string {
  if (count === null || count === undefined) return 'N/A';

  const rounded = Math.round(count);

  // For large counts, use abbreviated notation
  if (rounded >= 10000) {
    return `${(rounded / 1000).toFixed(1)}K pcs`;
  }

  return `${rounded} pcs`;
}

/** Text of a non-categorical X value (axis tick or tooltip): as-is, `N/A` when missing. */
export function formatXValue(value: string | null): string {
  return value !== null ? value.toString() : 'N/A';
}

/**
 * Get the appropriate formatter for a metric
 * @param metric - Metric key
 * @returns Formatter function
 */
export function getMetricFormatter(metric: string): (value: number | null) => string {
  switch (metric) {
    case 'totalMachiningDuration':
      return formatDuration;
    case 'pieceMachiningDuration':
      return formatDuration;
    case 'machinedVolume':
      return formatVolume;
    case 'machinedDistance':
      return formatDistance;
    case 'machinedPieces':
      return formatPieces;
    case 'malround':
      return formatMalround;
    case 'vc':
      return (value) => (value !== null ? value.toFixed(0) : 'N/A');
    case 'fz':
      return (value) => (value !== null ? value.toFixed(3) : 'N/A');
    case 'ap':
    case 'ae':
      return (value) => (value !== null ? value.toFixed(2) : 'N/A');
    default:
      return (value) => (value !== null ? value.toFixed(2) : 'N/A');
  }
}

/**
 * Get the unit suffix for a metric
 * @param metric - Metric key
 * @returns Unit string
 */
export function getMetricUnit(metric: string): string {
  switch (metric) {
    case 'totalMachiningDuration':
    case 'pieceMachiningDuration':
      return '';
    case 'machinedVolume':
      return 'cm³';
    case 'machinedDistance':
      return 'mm';
    case 'machinedPieces':
      return 'pcs';
    case 'malround':
      return 'µm';
    case 'vc':
      return 'm/min';
    case 'fz':
      return 'mm/z';
    case 'ap':
    case 'ae':
      return 'mm';
    default:
      return '';
  }
}

/**
 * Format tool holder type from API value to human-readable label
 * @param holderValue - Tool holder value from API (e.g., "frettage_chaud")
 * @returns Formatted tool holder name (e.g., "Hot Shrink" based on translation)
 */
export function formatToolHolderType(holderValue: string | null): string {
  if (!holderValue) return 'N/A';

  // Find the tool holder type
  const holderType = toolHolderTypes.find((type) => type.value === holderValue);

  if (!holderType) {
    // If not found, return the original value capitalized
    return holderValue
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  // Return the labelKey - this will need to be translated by the caller if needed
  // For now, return a readable version from the value
  const readableLabels: Record<string, string> = {
    frettage_chaud: 'Frettage chaud',
    frettage_froid: 'Frettage froid',
    pince_serrge: 'Pince serrée',
    pince_hydraulique: 'Pince hydraulique',
    autre: 'Autre',
  };

  return (
    readableLabels[holderValue] ||
    holderValue
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  );
}

/**
 * Format lubrication type from API value to human-readable label
 * @param lubValue - Lubrication value from API (e.g., "sec", "huile")
 * @returns Formatted lubrication name (e.g., "Sec", "Huile")
 */
export function formatLubricationType(lubValue: string | null): string {
  if (!lubValue) return 'N/A';

  // Find the lubrication type
  const lubType = lubricationTypes.find((type) => type.value === lubValue);

  if (!lubType) {
    // If not found, return the original value capitalized
    return lubValue.charAt(0).toUpperCase() + lubValue.slice(1);
  }

  // Return a readable version from the value
  const readableLabels: Record<string, string> = {
    sec: 'Sec',
    huile: 'Huile',
    emulsion: 'Émulsion',
    microlub: 'MQL',
    co2: 'CO₂',
    air: 'Air',
    other: 'Autre',
  };

  return readableLabels[lubValue] || lubValue.charAt(0).toUpperCase() + lubValue.slice(1);
}

/**
 * Format a company name to title case, capitalising the first character of each
 * word separated by spaces or hyphens.
 *
 * @param name - Raw company name (e.g. "HE-ARC INGENIERIE")
 * @returns Title-cased name (e.g. "He-Arc Ingenierie")
 *
 * @example
 * formatCompanyName('HE-ARC INGENIERIE') // "He-Arc Ingenierie"
 * formatCompanyName('acme corp')         // "Acme Corp"
 * formatCompanyName('')                  // ""
 */
export function formatCompanyName(name: string): string {
  return name.toLowerCase().replace(/(^|[\s-])(\S)/g, (_, sep, char) => sep + char.toUpperCase());
}
